/**
 * shared/access.ts — the predicate. One implementation, three call sites.
 *
 * LAW 13. The UI computes its rows from THIS FILE, never from a build flag, never from a tier string
 * it inferred, never from a `disabled` prop somebody hardcoded. `AB_ANON_REGEN` does not exist. Two
 * implementations of one rule diverge on the case nobody screenshotted.
 *
 * The client calls these to grey out UI, the server calls them again to enforce, and the WebSocket
 * calls them to admit. The client's answer is a courtesy; the server's is the authority.
 *
 * ⚠️ LAW 17. `canSee` is the UI predicate. It is NOT the authorising one for bytes. Every route that
 * serves bytes — audio, images, covers, downloads — calls the SERVER's `readAccess()`, which also
 * understands share tokens, guests and rooms, and which refuses 404 rather than 403. books shipped
 * the bug this prevents: `canSee()` correctly answers "no" for a PRIVATE book opened through a share
 * link, so the share page rendered its prose and then silently 404'd every illustration and all the
 * narration audio — the flagship flow, broken in the one case it was built for, with nothing in any
 * log. CI greps: `canSee(` must not appear in server/src/routes/{audio,images,covers,downloads}.ts.
 */

import type { BookVisibility } from "./doc.ts";
import type { MaybeActor } from "./actor.ts";

/**
 * The minimum a book row must expose to be judged. Written structurally so a drizzle row, a
 * `BookMeta`, or a three-field projection from the shelf query all satisfy it without a mapping step
 * — a mapping step is where a `takenDownAt` gets dropped.
 */
export interface AccessBook {
  ownerId: string;
  visibility: BookVisibility;
  deletedAt?: number | null;
  takenDownAt?: number | null;
  /**
   * P4. What a **live** share link held by *this* caller, on *this* book, confers right now.
   *
   * ⚠️ It rides on the BOOK and not on the actor, and that is the whole reason this stayed
   * synchronous on both sides. A grant is a fact about a *pair* — this reader, this book — so hanging
   * it off the actor would mean either one bag of grants for every book (wrong the moment somebody
   * holds two links) or an actor object that has to be rebuilt per book (a second identity seam).
   * Hanging it off the book means the server decorates the row it already loaded and the client reads
   * the field the API already sends it, and `canRegenerate` needs no new argument at any call site.
   *
   * Absent (`undefined`) on every row nobody resolved a link for — which is the overwhelming majority,
   * and is why it is optional rather than an empty array somebody has to remember to build.
   */
  grants?: readonly ShareGrantKind[] | null;
  /**
   * A live named-account grant resolved for THIS caller and THIS book.
   *
   * Like `grants`, this is request-local decoration rather than a stored property of the book. It
   * grants the reading surface only; `canEdit` deliberately ignores it. Absent is the safe default.
   */
  directAccess?: boolean;
}

export type ShareGrantKind = "regenerate" | "download" | "room";

/**
 * Does a share link held by this actor grant `kind` on this book?
 *
 * The grants are resolved **per lookup**, never baked into a signed blob (§6.4): on the server from
 * the `abk_share` cookie against `share_links`, checking `revokedAt`/`expiresAt` on every request; on
 * the client from the same field the API sends back. Revoking a link is therefore immediate rather
 * than "immediate once the token expires".
 *
 * Three guards, and each one is a hole somebody would otherwise have to remember to plug at the call
 * site:
 *
 *  1. **No actor, no grant.** `GET /s/:token` mints the guest cookie (LAW 15) precisely so that a
 *     link holder always has a subject — and every grant this returns spends something metered per
 *     subject (`regenerate` is 120/day; `download` writes an audit row). A grant with nobody to
 *     attribute it to is an unbounded one.
 *  2. **Deleted and taken down outrank a link.** `POST /admin/books/:id/takedown` revokes every link
 *     it can see, but a link created in the same millisecond, or one restored by a bad migration,
 *     must still refuse — the takedown is the authority, not the revocation sweep that follows it.
 *  3. **A grant is a capability, never an identity.** It says nothing about listing, and `canList`
 *     below does not consult it: a share link opens one book, it does not put that book in anybody's
 *     shelf.
 */
export function shareGrant(a: MaybeActor, b: AccessBook, kind: ShareGrantKind): boolean {
  if (!a) return false;
  if (b.deletedAt || b.takenDownAt) return false;
  return b.grants?.includes(kind) ?? false;
}

export const isGod = (a: MaybeActor): boolean => !!a && a.kind === "user" && a.isAdmin;

export const isOwner = (b: AccessBook, a: MaybeActor): boolean =>
  !!a && a.kind === "user" && b.ownerId === a.id;

/** Read the text, hear the audio, WITHOUT a token. */
export function canSee(b: AccessBook, a: MaybeActor): boolean {
  if (b.deletedAt) return false;
  if (b.takenDownAt) return isGod(a); // takedown outranks ownership
  return isGod(a) || isOwner(b, a) || (!!a && a.kind === "user" && b.directAccess === true) || b.visibility === "public" || b.visibility === "unlisted";
}

/**
 * Appear in ANY listing, search, sitemap or room lobby.
 *
 * The difference between this and `canSee` IS the definition of "unlisted", and that is why both live
 * in one file: an unlisted book is visible to anyone holding its id and invisible to everyone else.
 * The id is the capability (`bk_` + 16B base36, ~83 bits), which is also why `slug` is set only on
 * public books.
 */
export function canList(b: AccessBook, a: MaybeActor): boolean {
  if (b.deletedAt || b.takenDownAt) return isGod(a);
  return isGod(a) || isOwner(b, a) || (!!a && a.kind === "user" && b.directAccess === true) || b.visibility === "public";
}

export const canEdit = (b: AccessBook, a: MaybeActor): boolean =>
  !b.deletedAt && (isGod(a) || isOwner(b, a));

/** Quota is checked separately — this is the access question, not the budget question. */
export const canNarrate = (b: AccessBook, a: MaybeActor): boolean => canSee(b, a);

/**
 * ⚠️ Anon cannot regenerate, and this is a DELIBERATE NARROWING of Karti's stated "anon gets voice +
 * all features" — put to him and confirmed on 2026-08-06. On a public book an anonymous regenerate
 * changes the audio every other listener hears, with no undo, and it is a denial-of-wallet button on
 * one GPU. Anon keeps "Sounds wrong", which writes the identical telemetry row with `applied: false`,
 * so the signal is fully preserved. The escape hatch is per-share-link (`allowRegenerate`), not
 * per-tier — one column to reverse. Do not re-litigate it; it was asked and answered.
 */
export const canRegenerate = (b: AccessBook, a: MaybeActor): boolean =>
  canEdit(b, a) || shareGrant(a, b, "regenerate");

export const canJoinRoom = (b: AccessBook, a: MaybeActor): boolean => canSee(b, a);

/**
 * Caching for offline is `canSee`, not `canEdit`. The original `canDownload = canEdit` would have
 * made the entire offline story owner-only, and the browser is caching those bytes on every play
 * regardless.
 */
export const canCacheOffline = (b: AccessBook, a: MaybeActor): boolean => canSee(b, a);

/** Export is a FILE on disk (.m4b / .epub) and outlives every revocation, so it is the editor's right. */
export const canExport = (b: AccessBook, a: MaybeActor): boolean =>
  canEdit(b, a) || shareGrant(a, b, "download");
